import {
  Anchor,
  Bullet,
  Bullets,
  Callout,
  Fact,
  Internal,
  LegalDocument,
  Mail,
  P,
  SubHeading,
  type LegalSection,
} from '../../components/legal';
import { COMPANY, PAYMENTS, SELLER_NOTICE } from '../../lib/company';
import { usePageTitle } from '../../lib/usePageTitle';

/**
 * Terms of Service.
 *
 * These terms name the selling company and state plainly that we sell
 * directly: the payment gateway is a processor, not a reseller, so the contract
 * for the transaction is between the buyer and us. That distinction decides who
 * answers for an invoice, a refund and a tax question, and it is the thing a
 * buyer most needs to be able to find.
 *
 * Two things are deliberate. Prices are not restated here — they are served
 * from the plan catalogue and shown on the Pricing page, so repeating them in
 * a document nobody re-reads is how a site ends up contradicting itself.
 * And section 7 says out loud that the customer must be authorised to test the
 * systems they point the agent at; a QA agent that drives a browser against a
 * target is capable of being pointed somewhere it has no business being, and
 * silence on that would be a poor look in a dispute.
 *
 * This is a drafting starting point, not advice. It has not been reviewed by a
 * lawyer, and spec 76 still lists the commercial wording as an open item.
 */
const UPDATED = '2026-08-17';

const SECTIONS: LegalSection[] = [
  {
    id: 'who-we-are',
    title: 'Who you are contracting with',
    body: (
      <>
        <P>
          {COMPANY.product} is a software quality assurance service operated by{' '}
          <strong className="text-white">
            <Fact value={COMPANY.legalName} label="registered legal name" />
          </strong>
          {COMPANY.registrationNumber && <> (company number {COMPANY.registrationNumber})</>},
          trading as {COMPANY.product}, of{' '}
          <Fact value={COMPANY.registeredAddress} label="registered address" />.
        </P>
        <P>
          In these terms, &ldquo;we&rdquo;, &ldquo;us&rdquo; and &ldquo;our&rdquo; mean that
          company; &ldquo;you&rdquo; means the individual or organisation using the service.
          The service is provided at {COMPANY.domain} and through the {COMPANY.product} agent
          software you install.
        </P>
        <P>
          You can reach us at <Mail address={COMPANY.email.support} /> for support, or{' '}
          <Mail address={COMPANY.email.sales} /> for commercial questions. Full contact
          details are on the <Internal to="/support">support page</Internal>.
        </P>
      </>
    ),
  },
  {
    id: 'order-process',
    title: 'Our order process',
    body: (
      <>
        <Callout>{SELLER_NOTICE}</Callout>
        <P>
          The contract for your order is with us. We take the payment, issue the invoice or
          receipt, and handle cancellations and refunds under our{' '}
          <Internal to="/refund-policy">Refund Policy</Internal>. Card details are entered on
          a page operated by{' '}
          {PAYMENTS.gatewayName ?? <Fact value={null} label="payment gateway" />} and are
          processed by them; we do not receive or store your full card number.
        </P>
        <P>
          There is no reseller in between, so there is no third party to approach about an
          order and no separate set of buyer terms to accept. Anything to do with a payment
          &mdash; an invoice, an unrecognised charge, a refund, a cancellation &mdash; comes
          to <Mail address={COMPANY.email.support} />.
        </P>
      </>
    ),
  },
  {
    id: 'acceptance',
    title: 'Accepting these terms',
    body: (
      <>
        <P>
          By creating an account, or by purchasing or using the service, you agree to these
          terms, to our <Internal to="/refund-policy">Refund Policy</Internal> and to our{' '}
          <Internal to="/privacy">Privacy Policy</Internal>. If you do not agree, do not use
          the service.
        </P>
        <P>
          If you accept these terms on behalf of an organisation, you confirm that you are
          authorised to bind that organisation, and &ldquo;you&rdquo; means the organisation.
          You must be at least 18 years old to hold an account.
        </P>
      </>
    ),
  },
  {
    id: 'the-service',
    title: 'What the service does',
    body: (
      <>
        <P>
          {COMPANY.product} runs automated quality assurance against software you nominate
          and produces a report: a verdict on each acceptance criterion, together with the
          evidence that produced it. A subscription covers use of our hosted services and
          authorisation for your agents to run against them.
        </P>
        <P>
          What each plan includes — the number of concurrent agents and the execution hours
          per billing period — is set out on the{' '}
          <Internal to="/pricing">Pricing page</Internal> and shown against your account on
          your subscription dashboard. Those figures come from the same catalogue the
          platform enforces, so the dashboard is the authoritative statement of your current
          entitlement.
        </P>
        <P>
          We may change, add to or withdraw features. Where a change materially reduces what
          a paid plan provides, we will give you reasonable notice and you may{' '}
          <Anchor to="cancellation">cancel</Anchor>.
        </P>
      </>
    ),
  },
  {
    id: 'accounts',
    title: 'Your account and credentials',
    body: (
      <>
        <P>
          You must give accurate registration details and keep them current. You are
          responsible for everything done under your account, including by your agents and
          by anyone you give access to.
        </P>
        <Bullets>
          <Bullet>
            Keep your password and agent credentials secret. Agent credentials authorise
            metered execution against your subscription; treat them as you would a
            production secret.
          </Bullet>
          <Bullet>
            Tell us at <Mail address={COMPANY.email.security} /> as soon as you suspect a
            credential has been exposed, so it can be revoked.
          </Bullet>
          <Bullet>
            Do not share one account between people who should have separate access, and do
            not create additional accounts to exceed a plan limit.
          </Bullet>
        </Bullets>
      </>
    ),
  },
  {
    id: 'authorisation-to-test',
    title: 'Authorisation to test',
    body: (
      <>
        <P>
          The agent acts against whatever target you point it at. You must have the right to
          do that.
        </P>
        <Callout>
          You confirm that you own, or are authorised by the owner to test, every system,
          application, environment and account the agent is directed at, and that doing so
          does not breach any agreement or law that applies to you.
        </Callout>
        <P>
          You are responsible for the consequences of a run against a system you were not
          entitled to test, and for making sure runs against your own systems are safe — for
          example by using test environments and test data rather than production data
          belonging to your own customers.
        </P>
      </>
    ),
  },
  {
    id: 'acceptable-use',
    title: 'Acceptable use',
    body: (
      <>
        <P>You must not use the service to:</P>
        <Bullets>
          <Bullet>
            access, probe or disrupt any system you are not authorised to test, or conduct
            load, stress or denial-of-service activity against a third party;
          </Bullet>
          <Bullet>
            break any law, infringe anyone&rsquo;s rights, or process personal data you have
            no lawful basis to process;
          </Bullet>
          <Bullet>
            circumvent metering, entitlement checks, rate limits or any other technical
            control, or attempt to obtain agent capacity you have not paid for;
          </Bullet>
          <Bullet>
            resell, sublicense or provide the service to a third party as your own, or use it
            to build a competing product;
          </Bullet>
          <Bullet>
            upload malware, or use the agent as a channel to deliver anything harmful to a
            target system;
          </Bullet>
          <Bullet>
            interfere with the integrity or performance of the service or another
            customer&rsquo;s use of it.
          </Bullet>
        </Bullets>
        <P>
          Security research on {COMPANY.product} itself is welcome, but report findings
          privately to <Mail address={COMPANY.email.security} /> rather than testing against
          other customers.
        </P>
      </>
    ),
  },
  {
    id: 'fees',
    title: 'Fees, billing and renewal',
    body: (
      <>
        <P>
          Paid plans are billed in advance for each billing period at the price shown at
          checkout, and renew automatically for further periods until cancelled. Prices are
          stated in the currency shown at checkout.
        </P>
        <Bullets>
          <Bullet>
            <strong className="text-white">Payment.</strong> A paid plan becomes active on
            your account only once the payment processor confirms the payment to us — never
            on the browser&rsquo;s word — so a plan may briefly show as pending while that
            confirmation arrives.
          </Bullet>
          <Bullet>
            <strong className="text-white">Failed payments.</strong> If a renewal fails, it
            may be retried. We may suspend paid entitlements while a payment is outstanding,
            and cancel the subscription if it stays unpaid.
          </Bullet>
          <Bullet>
            <strong className="text-white">Price changes.</strong> We may change plan prices.
            A change takes effect at your next renewal and we will give you at least 30
            days&rsquo; notice by email beforehand, so you can cancel first if you prefer.
          </Bullet>
          <Bullet>
            <strong className="text-white">Usage beyond your plan.</strong> Execution hours
            and agent counts are entitlements, not commitments to bill you more. When an
            allowance is exhausted, runs stop until the next period or until you upgrade; we
            do not charge overage without your agreement.
          </Bullet>
        </Bullets>
      </>
    ),
  },
  {
    id: 'taxes',
    title: 'Taxes',
    body: (
      <P>
        Plan prices are quoted in {PAYMENTS.currency}. Where tax applies to your order, the
        amount and whether it is included in or added to the plan price is shown before you
        confirm payment &mdash; nothing is added afterwards. If your organisation needs a tax
        registration number to appear on the invoice, send it to{' '}
        <Mail address={COMPANY.email.support} /> and we will reissue the invoice with it.
      </P>
    ),
  },
  {
    id: 'free-plan',
    title: 'Free plan and trials',
    body: (
      <P>
        The Free plan is offered so you can run a real verification before committing to
        anything, and its allowances — including any promotional first-period allowance — are
        as described on the <Internal to="/pricing">Pricing page</Internal>. It is provided as
        is, may be changed or withdrawn, and we may suspend a free account that is being used
        to avoid paying for capacity or that has been inactive for an extended period.
      </P>
    ),
  },
  {
    id: 'professional-services',
    title: 'Professional setup and support services',
    body: (
      <>
        <P>
          The one-time setup engagement and the optional monthly support retainer described
          on the <Internal to="/pricing">Pricing page</Internal> are separate services. Buying
          either one does not include a {COMPANY.product} subscription, and a subscription
          does not include either of them.
        </P>
        <Bullets>
          <Bullet>
            Setup is quoted as a fixed fee with a target completion window, and is invoiced
            50% on commencement with the balance on the agreed milestone or completion.
          </Bullet>
          <Bullet>
            The monthly support retainer covers up to the stated number of hours in a month.
            Unused hours do not carry over. Either side may end it with 30 days&rsquo; notice.
          </Bullet>
          <Bullet>
            Scope, deliverables, dependencies on your team and any acceptance criteria are
            agreed in writing before work starts, and that written scope prevails over the
            summary on the Pricing page.
          </Bullet>
        </Bullets>
      </>
    ),
  },
  {
    id: 'cancellation',
    title: 'Cancellation, suspension and termination',
    body: (
      <>
        <P>
          You may cancel a subscription at any time. Cancellation stops future renewals; your
          plan stays active until the end of the period you have already paid for, and then
          reverts to the Free plan. Refunds are covered by our{' '}
          <Internal to="/refund-policy">Refund Policy</Internal>.
        </P>
        <P>
          We may suspend or terminate your access if you breach these terms, if payment goes
          unpaid, if we are required to by law, or if your use puts the service or another
          customer at risk. Where the circumstances allow it, we will tell you first and give
          you a chance to put things right.
        </P>
        <P>
          On termination your right to use the service ends. You can export your reports
          beforehand; see the <Internal to="/privacy">Privacy Policy</Internal> for how long
          data is kept afterwards.
        </P>
      </>
    ),
  },
  {
    id: 'your-data',
    title: 'Your data and our intellectual property',
    body: (
      <>
        <SubHeading>Yours</SubHeading>
        <P>
          You keep all rights in the specifications, test targets, configuration and data you
          bring, and in the reports and evidence the service produces for you. You grant us
          only the licence we need to operate the service for you — to host, process,
          transmit and display that material, and to hand relevant parts of it to the
          subprocessors listed in the <Internal to="/privacy">Privacy Policy</Internal>.
        </P>
        <SubHeading>Ours</SubHeading>
        <P>
          We keep all rights in the service, the agent software, our documentation and our
          brand. Nothing here transfers those to you. Where agent components are distributed
          under an open-source licence, that licence governs your use of those components.
        </P>
        <SubHeading>Feedback</SubHeading>
        <P>
          If you send us suggestions, we may use them without obligation or payment. We will
          not identify you as the source without asking.
        </P>
      </>
    ),
  },
  {
    id: 'third-parties',
    title: 'Third-party services',
    body: (
      <P>
        The service depends on third parties, including our payment processor, our cloud and
        email providers, and the language-model providers used to reason about test results.
        We choose them carefully and remain responsible to you for the service, but we do not
        control their availability and are not responsible for their own products or terms.
        The current list is in the <Internal to="/privacy">Privacy Policy</Internal>.
      </P>
    ),
  },
  {
    id: 'availability',
    title: 'Availability and support',
    body: (
      <P>
        We aim to keep the service available and to fix faults promptly, but we do not
        promise uninterrupted service on standard plans. Maintenance, upstream outages and
        faults happen. Support channels and target response times are on the{' '}
        <Internal to="/support">support page</Internal>. A contractual availability
        commitment or SLA is available only where one is agreed in writing on a Premium or
        enterprise engagement.
      </P>
    ),
  },
  {
    id: 'no-warranty',
    title: 'What the service does not promise',
    body: (
      <>
        <P>
          This one matters more than most, so it is stated plainly rather than buried: a
          passing report is evidence, not proof.
        </P>
        <Callout>
          {COMPANY.product} does not warrant that your software is free of defects, secure,
          compliant, or fit for any particular purpose. It reports what it observed against
          the criteria you gave it. Deciding what to release remains your engineering
          judgement and your responsibility.
        </Callout>
        <P>
          To the fullest extent the law allows, the service is provided &ldquo;as is&rdquo;
          and we exclude all implied warranties, including merchantability, fitness for a
          particular purpose and non-infringement. Automated reasoning can be wrong; treat a
          verdict as a finding to be checked, which is why every verdict ships with its
          evidence.
        </P>
        <P>
          Nothing in these terms excludes liability that cannot lawfully be excluded,
          including for death or personal injury caused by negligence, or for fraud. If you
          are a consumer, your statutory rights are unaffected.
        </P>
      </>
    ),
  },
  {
    id: 'liability',
    title: 'Limitation of liability',
    body: (
      <>
        <P>
          Subject to the paragraph above, neither party is liable for indirect or
          consequential loss, or for loss of profit, revenue, business, goodwill, anticipated
          savings or data, however caused.
        </P>
        <P>
          Our total liability arising out of or in connection with these terms, whether in
          contract, tort (including negligence) or otherwise, is limited in aggregate to the
          greater of the fees you paid us in the twelve months before the event giving rise
          to the claim, or USD 100.
        </P>
      </>
    ),
  },
  {
    id: 'indemnity',
    title: 'Indemnity',
    body: (
      <P>
        You will indemnify us against claims, losses and reasonable costs arising from your
        use of the service in breach of these terms — in particular from directing the agent
        at a system you were not authorised to test, or from material you supplied that
        infringed a third party&rsquo;s rights.
      </P>
    ),
  },
  {
    id: 'changes',
    title: 'Changes to these terms',
    body: (
      <P>
        We may update these terms. The date at the top shows when they last changed. For
        material changes affecting a paid plan we will give at least 30 days&rsquo; notice by
        email or in the product, and the change takes effect at your next renewal.
        Continuing to use the service after that means you accept the updated terms; if you
        do not, cancel before the change takes effect.
      </P>
    ),
  },
  {
    id: 'general',
    title: 'General',
    body: (
      <Bullets>
        <Bullet>
          <strong className="text-white">Assignment.</strong> You may not assign these terms
          without our consent. We may assign them to a successor of our business.
        </Bullet>
        <Bullet>
          <strong className="text-white">Severability.</strong> If a provision is
          unenforceable, the rest stands.
        </Bullet>
        <Bullet>
          <strong className="text-white">No waiver.</strong> Not enforcing a right does not
          waive it.
        </Bullet>
        <Bullet>
          <strong className="text-white">Entire agreement.</strong> These terms, the Refund
          Policy, the Privacy Policy and any written order or statement of work are the whole
          agreement between us.
        </Bullet>
        <Bullet>
          <strong className="text-white">Force majeure.</strong> Neither party is liable for
          failure caused by events outside its reasonable control.
        </Bullet>
      </Bullets>
    ),
  },
  {
    id: 'governing-law',
    title: 'Governing law',
    body: (
      <P>
        These terms are governed by the laws of{' '}
        <Fact value={COMPANY.governingLaw} label="governing law" />, and the courts of that
        place have exclusive jurisdiction over any dispute, except that either party may seek
        injunctive relief wherever necessary to protect its intellectual property. If you are
        a consumer, this does not deprive you of the protection of the mandatory law of the
        country you live in.
      </P>
    ),
  },
  {
    id: 'contact',
    title: 'How to contact us',
    body: (
      <>
        <Bullets>
          <Bullet>
            Support and account questions: <Mail address={COMPANY.email.support} />
            {COMPANY.supportPhone && <> or {COMPANY.supportPhone}</>}
          </Bullet>
          <Bullet>
            Sales, enterprise and professional services: <Mail address={COMPANY.email.sales} />
          </Bullet>
          <Bullet>
            Security reports: <Mail address={COMPANY.email.security} />
          </Bullet>
          <Bullet>
            Billing, invoices and refunds: <Mail address={COMPANY.email.support} />
          </Bullet>
        </Bullets>
        <P>
          Postal address:{' '}
          <Fact value={COMPANY.registeredAddress} label="registered address" />.
        </P>
      </>
    ),
  },
];

export function Terms() {
  usePageTitle('Terms of Service — WaveQA');

  return (
    <LegalDocument
      eyebrow="Legal"
      title="Terms of Service"
      intro={
        <>
          The agreement between you and {COMPANY.product} covering the subscription, what the
          service does and does not promise, and how billing works. Two clauses deserve
          reading before the rest: <Anchor to="authorisation-to-test">authorisation to test</Anchor>{' '}
          and <Anchor to="no-warranty">what the service does not promise</Anchor>.
        </>
      }
      updated={UPDATED}
      sections={SECTIONS}
    />
  );
}
